import type { MapLibreMap } from 'maplibre-gl';
import { RAINVIEWER_MAX_ZOOM, type RadarTileOptions, radarCoverageTileUrl } from '@repo/shared';
import { coverageGapsGeoJson } from './coverage-gaps';
import { firstSymbolLayerId } from './radar-layers';

/**
 * The radar-coverage scrim.
 *
 * An empty patch of map means one of two very different things: nothing is
 * falling there, or nothing is watching there. RainViewer is a mosaic of
 * national radar networks, so its footprint is a union of range rings with real
 * gaps between them — open ocean, and countries with no public radar feed. The
 * radar frames themselves cannot express that difference; both cases render as
 * clear sky.
 *
 * This layer draws RainViewer's coverage mask over the map to make the gaps
 * visible. The mask needs no recolouring: covered pixels are fully transparent
 * and uncovered pixels are opaque black, so drawing it at partial opacity dims
 * exactly the unobserved areas and leaves observed ground untouched.
 *
 * The mask is RainViewer's own account of itself, though, and it overstates:
 * a registered radar that sends no data still publishes a footprint. So the
 * scrim is two layers — the mask, and a fill over the regions in
 * `lib/coverage-gaps.ts` where that has been observed and confirmed.
 *
 * Like the radar layers this lives outside React, because MapLibre owns its own
 * mutable scene graph.
 */

export const COVERAGE_SOURCE_ID = 'radar-coverage-source';
export const COVERAGE_LAYER_ID = 'radar-coverage-layer';

/**
 * The scrim's second half: regions RainViewer marks as covered and has no data
 * for. See `lib/coverage-gaps.ts` for why the mask alone is not enough.
 */
export const COVERAGE_GAP_SOURCE_ID = 'radar-coverage-gap-source';
export const COVERAGE_GAP_LAYER_ID = 'radar-coverage-gap-layer';

/**
 * How hard to dim unobserved areas. Enough to read as deliberately greyed out
 * at a glance, but not so much that the basemap underneath becomes unusable —
 * a viewer in an uncovered area still needs to navigate.
 */
export const COVERAGE_SCRIM_OPACITY = 0.4;

export interface CoverageLayerConfig {
  visible: boolean;
  tileSize: RadarTileOptions['tileSize'];
  opacity?: number;
}

/**
 * Adds, removes and updates the scrim to match `config`. Safe to call
 * repeatedly — it diffs against what is already on the map.
 *
 * Ordering against the radar layers is deliberately not managed. The scrim is
 * transparent everywhere the radar has data, so the two never meaningfully
 * overlap and whichever ends up on top looks the same. Both go below the first
 * symbol layer, which is the ordering that does matter: place labels have to
 * stay readable through the dimming.
 *
 * For the mask that is a property of the bytes — transparent where observed —
 * but the gap fill is opaque black over a whole region, so the same guarantee
 * now rests on an invariant instead: **a listed outline must only cover ground
 * that returns nothing.** A polygon drawn a little too generously would dim
 * live echoes, and neither this module nor the test suite can tell. It is
 * checked where it is established, against the tiles — see the verification
 * protocol in `lib/coverage-gaps.ts`.
 */
export function syncCoverageLayer(map: MapLibreMap, config: CoverageLayerConfig): void {
  const { visible, tileSize, opacity = COVERAGE_SCRIM_OPACITY } = config;

  if (!visible) {
    removeCoverageLayer(map);
    return;
  }

  if (!map.getSource(COVERAGE_SOURCE_ID)) {
    map.addSource(COVERAGE_SOURCE_ID, {
      type: 'raster',
      tiles: [radarCoverageTileUrl(tileSize)],
      tileSize,
      minzoom: 0,
      // Past RainViewer's ceiling the mask is a "Zoom Level Not Supported"
      // card like any other tile; MapLibre overzooms the last real one instead.
      maxzoom: RAINVIEWER_MAX_ZOOM,
    });
  }

  // Read before anything is added: every insert goes below this same layer, so
  // the answer must not depend on how far through the sync we are.
  const beforeId = firstSymbolLayerId(map);

  if (!map.getLayer(COVERAGE_LAYER_ID)) {
    map.addLayer(
      {
        id: COVERAGE_LAYER_ID,
        type: 'raster',
        source: COVERAGE_SOURCE_ID,
        paint: {
          'raster-opacity': opacity,
          // The scrim is a mask, not imagery — cross-fading it between zoom
          // levels makes the coverage edge pulse on every zoom step.
          'raster-fade-duration': 0,
        },
      },
      beforeId,
    );
  } else {
    map.setPaintProperty(COVERAGE_LAYER_ID, 'raster-opacity', opacity);
  }

  if (!map.getSource(COVERAGE_GAP_SOURCE_ID)) {
    map.addSource(COVERAGE_GAP_SOURCE_ID, {
      type: 'geojson',
      data: coverageGapsGeoJson(),
    });
  }

  if (!map.getLayer(COVERAGE_GAP_LAYER_ID)) {
    map.addLayer(
      {
        id: COVERAGE_GAP_LAYER_ID,
        type: 'fill',
        source: COVERAGE_GAP_SOURCE_ID,
        paint: {
          // The same opaque black the mask uses, at the same opacity, so a
          // listed gap reads as one more uncovered area rather than as a
          // separate kind of thing. Where a listed region overlaps ground the
          // mask already marks uncovered the two stack and that patch goes
          // darker — accepted deliberately: both states mean "not observed",
          // and trimming the polygons to RainViewer's exact ring edges would be
          // precision about a boundary that is itself the thing we distrust.
          'fill-color': '#000000',
          'fill-opacity': opacity,
        },
      },
      beforeId,
    );
  } else {
    map.setPaintProperty(COVERAGE_GAP_LAYER_ID, 'fill-opacity', opacity);
  }
}

/** Removes the scrim, e.g. when the viewer turns it off. */
export function removeCoverageLayer(map: MapLibreMap): void {
  if (map.getLayer(COVERAGE_LAYER_ID)) map.removeLayer(COVERAGE_LAYER_ID);
  if (map.getSource(COVERAGE_SOURCE_ID)) map.removeSource(COVERAGE_SOURCE_ID);
  if (map.getLayer(COVERAGE_GAP_LAYER_ID)) map.removeLayer(COVERAGE_GAP_LAYER_ID);
  if (map.getSource(COVERAGE_GAP_SOURCE_ID)) map.removeSource(COVERAGE_GAP_SOURCE_ID);
}
